import type React from 'react';
import { useTranslation } from 'react-i18next';

import { ImageCarousel } from '@/components/ui/image-carousel';
import useClickOutside from '@/hooks/use-click-outside';
import type { useModal } from '@/components/modals/new/use-modal';
import { Modal } from '@/components/modals/new/modal';
import { Dialog, DialogContent, DialogTitle } from '@/components/new-ui/dialog';

type ViewMediaImage = {
  creatorId?: string | null;
  deletedAt?: string | null;
  estateId?: string | null;
  fileName: string | null;
  name: string | null;
  id: string;
  createdAt?: string | null;
  mediaId: string | null;
  updatedAt?: string | null;
  fileType?: string | null;
  isUsedByFlatfinder?: boolean;
};

export interface ViewMediaModalProps {
  images: ViewMediaImage[];
  preselectedCurrentIndex: number;
  showFileName?: boolean;
  imageBeautificationButtons?: boolean;
  getImageSrc?: (image: ViewMediaImage) => string;
  estateId?: string;
  projectId?: string;
  modal: ReturnType<typeof useModal>;
  layout?: 'stacked' | 'split';
}

export const ViewMediaModal: React.FC<ViewMediaModalProps> = (props) => {
  const modalContainerRef = useClickOutside<HTMLDivElement>(props.modal.dismiss, true);
  const { t } = useTranslation();

  const carousel = (
    <ImageCarousel
      key={`${props.images?.[props.preselectedCurrentIndex]?.mediaId ?? 'no-media'}-${props.preselectedCurrentIndex}`}
      {...props}
      estateId={props.estateId}
      projectId={props.projectId}
      onCloseModal={props.modal.dismiss}
      imageBeautificationButtons={props.imageBeautificationButtons ?? true}
      getImageSrc={props.getImageSrc}
      layout={props.layout ?? 'stacked'}
    />
  );

  if (props.layout === 'split') {
    const preselected = props.images?.[props.preselectedCurrentIndex];

    return (
      <Dialog
        open={props.modal.isOpen}
        onOpenChange={(open) => {
          if (!open) props.modal.dismiss();
        }}
      >
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-6xl flex-col gap-0 overflow-y-auto overflow-x-hidden p-0 sm:flex-row sm:overflow-hidden">
          <DialogTitle className="sr-only">
            {preselected?.name || preselected?.fileName || t('image')}
          </DialogTitle>
          {carousel}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Modal
      ref={modalContainerRef}
      className="flex max-h-full w-[90vw] max-w-[1200px] flex-col overflow-hidden !p-4"
      {...props.modal}
      title=""
    >
      <Modal.Body>
        <div className="flex h-full items-center justify-center overflow-hidden">{carousel}</div>
      </Modal.Body>
    </Modal>
  );
};
